import { useState, useEffect } from "react";
import { API_BASE } from "./config";

type PageLoginProps = {
  onSuccess: () => void;
};

export function PageLogin({ onSuccess }: PageLoginProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).has("p")
  );

  const attemptLogin = async (p: string) => {
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: p }),
        credentials: "include",
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        onSuccess();
        return;
      }
      setError(data.error ?? "Anmeldung fehlgeschlagen");
    } catch {
      setError("Netzwerkfehler");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const p = params.get("p");
    if (p) {
      attemptLogin(p);
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || loading) return;
    attemptLogin(password);
  };

  const params = new URLSearchParams(window.location.search);
  const hasAutologin = params.has("p");

  if (hasAutologin && loading) {
    return (
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="w-10 h-10 border-2 border-[var(--wedding-gold)] border-t-transparent rounded-full animate-spin" />
        <p className="text-[var(--wedding-ink-muted)]">Anmeldung läuft…</p>
      </div>
    );
  }

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl bg-[var(--wedding-white)] shadow-lg border border-[var(--wedding-blush-strong)] overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-[var(--wedding-gold-light)] to-[var(--wedding-gold)]" />
        <div className="p-6 sm:p-8">
          <h1 className="font-wedding-heading text-2xl font-semibold text-center text-[var(--wedding-ink)] mb-1">
            Gästefotos
          </h1>
          <p className="text-center text-[var(--wedding-ink-muted)] text-sm mb-6">
            Passwort eingeben, um zur Galerie zu gelangen
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="password" className="sr-only">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort"
                autoComplete="current-password"
                className="w-full rounded-xl border border-[var(--wedding-blush-strong)] bg-[var(--wedding-cream)] px-4 py-3 text-[var(--wedding-ink)] placeholder:text-[var(--wedding-ink-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--wedding-gold)] focus:border-transparent transition-shadow"
                disabled={loading}
              />
            </div>
            {error && (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="w-full rounded-xl bg-[var(--wedding-gold)] px-4 py-3 text-white font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--wedding-gold)] focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none transition-opacity"
            >
              {loading ? "Anmeldung läuft…" : "Anmelden"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
